import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useDarkMode } from "../contexts/DarkModeContext";
import { describeSource } from "./previewUtils";

// Extensions browsers won't reliably render inline via <iframe> because cloud
// storage often serves them with Content-Disposition: attachment (forcing a
// download) or octet-stream content-types (blank frame). We fetch the bytes
// and render them in-modal as text instead.
const TEXT_EXTS = new Set([
  "txt", "json", "csv", "tsv", "md", "markdown", "log", "xml", "yaml", "yml",
  "ini", "cfg", "conf", "toml", "sql", "py", "js", "jsx", "ts", "tsx",
  "html", "htm", "css", "sh", "env",
]);

function prettyPrint(text, ext) {
  if (ext === "json") {
    try { return JSON.stringify(JSON.parse(text), null, 2); } catch { /* keep raw */ }
  }
  return text;
}

// Generic fallback preview used when a citation's source isn't one of the
// purpose-built types (DOCX / video / PDF-with-highlight). For text-like
// files we fetch the bytes through the auth proxy and render them inline —
// signed cloud URLs frequently force a download via Content-Disposition,
// which leaves a bare iframe blank. Binary fallback still uses an iframe so
// browsers render PDFs/images inline.
export default function FilePreviewModal({ docName, previewUrl, fullDocUrl, downloadUrl, source, provider, authHeader, ext, onClose }) {
  const { isDarkMode } = useDarkMode();
  const isText = TEXT_EXTS.has((ext || "").toLowerCase());

  const [textContent, setTextContent] = useState(null);
  const [textError, setTextError] = useState(null);
  const [loading, setLoading] = useState(isText);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (!isText) return undefined;
    let cancelled = false;
    setLoading(true);
    setTextError(null);
    // Prefer the auth-proxy URL so the bearer header authenticates the
    // request; cloud signed URLs would also work but may send a download
    // disposition that's fine to ignore here since we read the body as text.
    // Only send auth credentials when fetching through the same-origin proxy
    // (downloadUrl). Signed cloud URLs (previewUrl fallback) must NOT receive
    // the Authorization header or cookies — cloud providers reject requests
    // that carry both a query-string signature and an Authorization header
    // (HTTP 400), and sending the token to external domains leaks credentials.
    const fetchUrl = downloadUrl || previewUrl;
    const isProxy = Boolean(downloadUrl);
    fetch(fetchUrl, {
      credentials: isProxy ? "include" : "omit",
      headers: isProxy && authHeader ? { Authorization: authHeader } : undefined,
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((text) => {
        if (cancelled) return;
        setTextContent(prettyPrint(text, (ext || "").toLowerCase()));
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setTextError(err.message || "Failed to load file");
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isText, downloadUrl, previewUrl, authHeader, ext]);

  const sourceLabel = describeSource(source, provider);
  const signedUrl = fullDocUrl || downloadUrl;

  const body = useMemo(() => {
    if (!isText) {
      return (
        <iframe
          src={previewUrl}
          title={docName}
          className="w-full h-[78vh] border-0"
        />
      );
    }
    if (loading) {
      return (
        <div className={`w-full h-[78vh] flex items-center justify-center text-sm ${
          isDarkMode ? "text-slate-400" : "text-gray-500"
        }`}>
          Loading preview…
        </div>
      );
    }
    if (textError) {
      return (
        <div className={`w-full h-[78vh] flex items-center justify-center text-sm ${
          isDarkMode ? "text-rose-300" : "text-rose-600"
        }`}>
          Could not load file: {textError}
        </div>
      );
    }
    // Rendered as a <div> rather than <pre> because a global stylesheet rule
    // forces `pre { background: ... !important }` which would override the
    // dark-mode background here.
    return (
      <div
        className="w-full h-[78vh] overflow-auto p-4 text-xs font-mono whitespace-pre-wrap break-words"
        style={{
          backgroundColor: isDarkMode ? "#0f172a" : "#f9fafb",
          color: isDarkMode ? "#f1f5f9" : "#111827",
        }}
      >
        {textContent}
      </div>
    );
  }, [isText, loading, textError, textContent, previewUrl, docName, isDarkMode]);

  return (
    <>
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9998] transition-opacity"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Preview of ${docName}`}
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      >
        <div
          className={`relative w-full max-w-5xl max-h-[90vh] rounded-xl shadow-2xl flex flex-col ${
            isDarkMode ? "bg-slate-800" : "bg-white"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={`flex items-center justify-between px-5 py-3 border-b ${
            isDarkMode ? "border-slate-700" : "border-gray-200"
          }`}>
            <div className="flex-1 mr-4 min-w-0">
              <h3 className={`text-sm font-semibold truncate ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}>{docName}</h3>
              <div className={`text-xs mt-0.5 truncate ${
                isDarkMode ? "text-slate-400" : "text-gray-500"
              }`}>
                Inline preview &middot; full document hosted on {sourceLabel}
              </div>
              {signedUrl && (
                <a
                  href={signedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={signedUrl}
                  className={`block text-[11px] mt-0.5 truncate font-mono underline ${
                    isDarkMode ? "text-sky-400 hover:text-sky-300" : "text-sky-600 hover:text-sky-700"
                  }`}
                >
                  {signedUrl}
                </a>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className={`p-1 rounded-lg transition-colors ${
                isDarkMode
                  ? "hover:bg-slate-700 text-slate-400 hover:text-slate-200"
                  : "hover:bg-gray-100 text-gray-500 hover:text-gray-700"
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className={`flex-1 min-h-[200px] ${isDarkMode ? "bg-slate-900" : "bg-white"}`}>
            {body}
          </div>
        </div>
      </div>
    </>
  );
}

FilePreviewModal.propTypes = {
  docName: PropTypes.string.isRequired,
  previewUrl: PropTypes.string.isRequired,
  fullDocUrl: PropTypes.string,
  downloadUrl: PropTypes.string,
  source: PropTypes.oneOf(["connector", "local", "proxy"]).isRequired,
  provider: PropTypes.string,
  authHeader: PropTypes.string,
  ext: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};
